import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Modal,
  Snackbar,
  Typography,
} from "@mui/material";
import { EditRoleForm } from "./edit-role-form";
import backIcon from "../../../image/back-icon.svg";
import editIcon from "../../../image/edit-modal-icon.svg";
import saveIcon from "../../../image/save-icon.svg";
import { useQuery, useQueryClient } from "react-query";
import { getRole, patchRole } from "../../../api/role";

const styleEdit = {
  position: "absolute",
  top: "0",
  right: "0",
  width: "576px",
  height: "100vh",
  overflowY: "scroll",
  bgcolor: "#FFF",
  border: "0px",
  boxShadow: 24,
  padding: "0",
  borderRadius: "8px",
  margin: "0 -10px",
};

export const EditAdminRoleModal = (props) => {
  const { id: roleId, isOpen, onClose } = props;
  const { data: role } = useQuery(["role", roleId], () => getRole(roleId), {
    enabled: isOpen,
  });

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      className="TermsModal"
      sx={{
        background:
          "linear-gradient(90deg, rgba(15, 127, 144, 0.8) -8.75%, rgba(0, 176, 138, 0.8) 113.12%);",
      }}
    >
      {!!role && isOpen ? (
        <EditAdminRole {...props} role={role.role} />
      ) : (
        <Box sx={styleEdit}>Loading....</Box>
      )}
    </Modal>
  );
};

export const EditAdminRole = ({ onClose, role, user, id }) => {
  const roleId = id;
  const [isEdit, setIsEdit] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [openSnackbarSucces, setOpenSnackbarSucces] = useState(false);
  const [isErrorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const closeErrorSnackbar = () => setErrorSnackbarOpen(false);
  const title = role?.name ?? "";
  const [payloadToSubmit, setPayloadToSubmit] = useState({});
  const queryClient = useQueryClient();
  const [policiesToRemove, setPoliciesToRemove] = useState([]);
  const [policiesToAdd, setPoliciesToAdd] = useState([]);
  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackbarSucces(false);
  };
  const removePoliciesFromRoles = (roleId) => {
    if (policiesToAdd.find((entry) => entry.id === roleId)) {
      setPoliciesToAdd((prevEntries) =>
        prevEntries.filter((entry) => entry.id !== roleId)
      );
    } else {
      setPoliciesToRemove((prevPolicie) => [roleId, ...prevPolicie]);
    }
  };
  const addPoliciesToRoles = (policiesToAdd) => {
    // deduplicate repeated files
    setPoliciesToAdd((prevPolicies) => [policiesToAdd, ...prevPolicies]);
  };
  const submitForm = async (payload) => {
    payload = payload ?? payloadToSubmit;
    try {
      await patchRole(payload);
      setIsEdit(false);
      setDisabled(false);
      queryClient.invalidateQueries(["role", roleId]);
      queryClient.invalidateQueries(["roles"]);
      queryClient.invalidateQueries(["role"]);
      // prevent optimistic update
      resetState();
    } catch (e) {
      console.log(e);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    // The new array of files that should be set to the collection
    const newPoliciesArray = [
      ...role.policies.filter(({ id }) => !policiesToRemove.includes(id)),
      ...policiesToAdd,
    ];
    const formData = new FormData(e.target);
    const payload = {
      id: roleId,
      policies: newPoliciesArray.map(({ id }) => id),
      overwrite: true,
    };
    for (const [key, value] of formData.entries()) {
      payload[key] = value;
    }
    setPayloadToSubmit(payload);
    submitForm(payload);
  };
  const resetState = () => {
    setPoliciesToRemove([]);
    setPoliciesToAdd([]);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={styleEdit}>
        <Grid
          container
          sx={{
            height: "80px",
            justifyContent: "space-between",
            alignItems: "center",
            background:
              "linear-gradient(75.61deg, rgba(244, 144, 77, 0.4) 3.76%, rgba(243, 139, 151, 0.4) 51.01%, rgba(15, 127, 144, 0.4) 98.26%);",
          }}
        >
          <Grid
            item
            sx={{
              width: "450px",
              maxWidth: "80%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "24px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              Role - {title}
            </Typography>
          </Grid>
          <Grid item sx={{ maxWidth: "20%" }}>
            <Button
              sx={{ marginRight: "22px" }}
              disabled={disabled}
              onClick={() => {
                setIsEdit(true);
                setDisabled(true);
              }}
            >
              {isEdit ? (
                "Editing"
              ) : (
                <>
                  <img
                    src={editIcon}
                    alt="Edit icon"
                    style={{ margin: "0 6px 0 0" }}
                  />{" "}
                  Edit{" "}
                </>
              )}
            </Button>
          </Grid>
        </Grid>
        <Snackbar
          open={openSnackbarSucces}
          onClose={handleClose}
          autoHideDuration={5000}
          sx={{
            zIndex: "10",
            "& .MuiSnackbarContent-root": {
              fontSize: "16px",
              minWidth: "230px",
              display: "flex",
              justifyContent: "center",
              anchorOrigin: "bottom, right",
            },
          }}
        >
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{
              width: "auto",
              position: "fixed",
              right: "24px",
              bottom: "20px",
              fontSize: "16px",
            }}
          >
            Role updated successfully
          </Alert>
        </Snackbar>
        <Snackbar
          open={isErrorSnackbarOpen}
          onClose={closeErrorSnackbar}
          autoHideDuration={5000}
          sx={{
            zIndex: "10",
            "& .MuiSnackbarContent-root": {
              fontSize: "16px",
              minWidth: "230px",
              display: "flex",
              justifyContent: "center",
              anchorOrigin: "bottom, right",
            },
          }}
        >
          <Alert
            onClose={closeErrorSnackbar}
            severity="error"
            sx={{
              width: "auto",
              position: "fixed",
              right: "24px",
              bottom: "20px",
              fontSize: "16px",
            }}
          >
            "Error"
          </Alert>
        </Snackbar>
        <EditRoleForm
          role={role}
          isEdit={isEdit}
          onClose={onClose}
          policiesToAdd={policiesToAdd}
          policiesToRemove={policiesToRemove}
          removePoliciesFromRoles={removePoliciesFromRoles}
          addPoliciesToRoles={addPoliciesToRoles}
        />
        <Box
          sx={{
            width: "100%",
            borderTop: "1px solid #B0C9CB ",
          }}
        >
          {isEdit ? (
            <>
              <Button
                className="userLink"
                onClick={() => {
                  setIsEdit(false);
                  setDisabled(false);
                  setPoliciesToAdd([]);
                }}
                sx={{
                  margin: "0 0 0 24px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  outline: "4px solid rgba(176, 201, 203, 1)",
                  margin: "24px",
                }}
              >
                <img
                  src={saveIcon}
                  alt="close icon"
                  style={{ margin: "0 9px 0 0" }}
                />{" "}
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                setIsEdit(false);
                setDisabled(false);
                onClose(true);
              }}
              sx={{
                outline: "4px solid rgba(176, 201, 203, 1)",
                margin: "24px",
              }}
            >
              <img
                src={backIcon}
                alt="close icon"
                style={{ margin: "0 9px 0 0" }}
              />{" "}
              Back
            </Button>
          )}
        </Box>
      </Box>
    </form>
  );
};
